import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Select from "react-select";
import { format } from "date-fns";

import API_BASE_URL from "../../../config/config.js";
import axios from "axios";

// toast
import { toast } from "react-hot-toast";

const createInitialFormData = (companyMode) => ({
    ...(companyMode ? {} : { idchampionship: "" }),
    name: "",
    periodo: "",
    startdate: "",
    enddate: "",
    image_path: "",
    place: "",
    iddepartamento: "",
});

const ACCEPTED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/avif",
];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const PERIODS_CATALOGUE_URL = "https://cloud-master.perufedup.com/public/catalogue/MjA=";

const ModalCreateEvent = ({
    open,
    setOpen,
    idcompany,
    iduser,
    companyMode = false,
    deportes = [],
    onCreated,
}) => {
    const [formData, setFormData] = useState(
        createInitialFormData(companyMode)
    );
    const [selectedSports, setSelectedSports] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");

    const [departamentos, setDepartamentos] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [loadingPeriodos, setLoadingPeriodos] = useState(false);
    const [loading, setLoading] = useState(false); // Para manejar el estado de carga
    const [message, setMessage] = useState(""); // Para mostrar mensajes de éxito o error

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDepartamentoChange = (selectedOption) => {
        setFormData({ ...formData, iddepartamento: selectedOption?.value || "" });
    };

    const handlePeriodoChange = (selectedOption) => {
        // El endpoint de creación espera el nombre del catálogo, no su id.
        setFormData((current) => ({
            ...current,
            periodo: selectedOption?.name ? String(selectedOption.name) : "",
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            setImageFile(null);
            setImagePreview("");
            return;
        }

        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            toast.error("Formato no permitido. Use JPG, JPEG, PNG, WEBP, GIF o AVIF.");
            setImageFile(null);
            setImagePreview("");
            e.target.value = "";
            return;
        }

        if (file.size > MAX_IMAGE_SIZE) {
            toast.error("La imagen no puede superar los 5 MB.");
            setImageFile(null);
            setImagePreview("");
            e.target.value = "";
            return;
        }

        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (companyMode && selectedSports.length === 0) {
            toast.error("Debe seleccionar al menos un deporte.");
            return;
        }

        if (companyMode && !formData.periodo) {
            toast.error("Debe seleccionar un periodo.");
            return;
        }

        if (companyMode && !imageFile) {
            toast.error("Debe seleccionar una imagen para el evento.");
            return;
        }

        setLoading(true);
        setMessage("");

        // Convertimos las fechas al formato adecuado
        const formattedStartDate = formData.startdate
            ? format(new Date(formData.startdate), "yyyy-MM-dd 00:00:00")
            : "";
        const formattedEndDate = formData.enddate
            ? format(new Date(formData.enddate), "yyyy-MM-dd 00:00:00")
            : "";

        const normalizedData = {
            ...formData,
            ...(companyMode ? { idcompany, iduser } : {}),
            startdate: formattedStartDate,
            enddate: formattedEndDate,
        };

        let payload = normalizedData;

        if (companyMode) {
            payload = new FormData();
            Object.entries(normalizedData).forEach(([key, value]) => {
                if (key !== "image_path") payload.append(key, value ?? "");
            });
            selectedSports.forEach((sport) => payload.append("sports", sport.value));
            payload.append("image", imageFile);
        }

        try {
            const endpoint = companyMode
                ? "/api/hayllis/v2/championshipv2"
                : "/championship";
            const response = await axios.post(`${API_BASE_URL}${endpoint}`, payload);
            setMessage(
                response.data?.data?.message ||
                response.data?.message ||
                response.data?.msg ||
                "Evento creado exitosamente."
            );
            setOpen(false); // Cierra el modal
            setFormData(createInitialFormData(companyMode)); // Reinicia el formulario
            setSelectedSports([]);
            setImageFile(null);
            setImagePreview("");
            toast.success("¡Evento Creado Exitosamente!");
            await onCreated?.(response.data);
        } catch (error) {
            const errorMessage =
                error.response?.data?.msg ||
                error.response?.data?.message ||
                "Error al registrar el campeonato";
            setMessage(errorMessage);
            toast.error(errorMessage);
            console.error("Error en crear un campeonato", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const getDepartamentos = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/departamento`);
                const options = response.data.map((dep) => ({
                    value: dep.iddepartamento,
                    label: `${dep.iddepartamento} - ${dep.name}`,
                }));
                setDepartamentos(options);
            } catch (error) {
                console.error("Error al traer departamentos", error);
            }
        };

        getDepartamentos();
    }, []);

    useEffect(() => {
        if (!companyMode || !open) return;

        let active = true;

        const getPeriodos = async () => {
            setLoadingPeriodos(true);

            try {
                const response = await axios.get(PERIODS_CATALOGUE_URL, {
                    withCredentials: false,
                });
                const catalogue = Array.isArray(response.data) ? response.data : [];
                const options = catalogue
                    .filter((periodo) => periodo?.name != null)
                    .map((periodo) => ({
                        ...periodo,
                        name: String(periodo.name),
                    }))
                    .sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));

                if (active) setPeriodos(options);
            } catch (error) {
                if (active) {
                    setPeriodos([]);
                    toast.error("No se pudieron cargar los periodos.");
                }
                console.error("Error al traer periodos", error);
            } finally {
                if (active) setLoadingPeriodos(false);
            }
        };

        getPeriodos();

        return () => {
            active = false;
        };
    }, [companyMode, open]);

    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    return (
        <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={open}
            onClose={() => setOpen(false)}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: { xs: "flex-start", sm: "center" },
                p: { xs: 1.5, sm: 3 },
            }}
        >
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: 760,
                    width: "100%",
                    borderRadius: "md",
                    p: { xs: 2, sm: 3 },
                    boxShadow: "lg",
                    maxHeight: { xs: "calc(100dvh - 24px)", sm: "calc(100dvh - 48px)" },
                    overflowY: "auto",
                }}
            >
                <ModalClose variant="plain" sx={{ m: 1 }} />
                <Typography
                    component="h2"
                    id="modal-title"
                    level="h4"
                    textColor="inherit"
                    sx={{ fontWeight: "lg", mb: { xs: 2, sm: 3 }, textAlign: "center" }}
                >
                    Crear evento
                </Typography>
                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2"
                >
                    {!companyMode && (
                        <div className="flex flex-col">
                            <label htmlFor="idchampionship" className="text-sm font-medium text-gray-700">
                                ID Championship
                            </label>
                            <input
                                type="text"
                                id="idchampionship"
                                name="idchampionship"
                                value={formData.idchampionship}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                                required
                            />
                        </div>
                    )}

                    <div className="flex flex-col">
                        <label htmlFor="name" className="text-sm font-medium text-gray-700">
                            Nombre
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="periodo" className="text-sm font-medium text-gray-700">
                            Periodo
                        </label>
                        {companyMode ? (
                            <Select
                                inputId="periodo"
                                name="periodo"
                                options={periodos}
                                value={periodos.find((periodo) => periodo.name === formData.periodo) || null}
                                onChange={handlePeriodoChange}
                                getOptionLabel={(periodo) => periodo.name}
                                getOptionValue={(periodo) => periodo.name}
                                placeholder={loadingPeriodos ? "Cargando periodos..." : "Seleccione un periodo"}
                                noOptionsMessage={() => loadingPeriodos ? "Cargando..." : "No hay periodos disponibles"}
                                isLoading={loadingPeriodos}
                                isDisabled={loadingPeriodos}
                                isSearchable
                                className="mt-1"
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        padding: "4px",
                                        borderRadius: "0.375rem",
                                        borderColor: "#d1d5db",
                                        boxShadow: "none",
                                        "&:hover": { borderColor: "#3b82f6" },
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        ) : (
                            <input
                                type="text"
                                id="periodo"
                                name="periodo"
                                value={formData.periodo}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                                required
                            />
                        )}
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="startdate" className="text-sm font-medium text-gray-700">
                            Fecha de inicio
                        </label>
                        <input
                            type="date"
                            id="startdate"
                            name="startdate"
                            value={formData.startdate}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="enddate" className="text-sm font-medium text-gray-700">
                            Fecha de fin
                        </label>
                        <input
                            type="date"
                            id="enddate"
                            name="enddate"
                            value={formData.enddate}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="place" className="text-sm font-medium text-gray-700">
                            Lugar
                        </label>
                        <input
                            type="text"
                            id="place"
                            name="place"
                            value={formData.place}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="iddepartamento" className="text-sm font-medium text-gray-700">
                            Departamento
                        </label>
                        <Select
                            id="iddepartamento"
                            name="iddepartamento"
                            options={departamentos}
                            onChange={handleDepartamentoChange}
                            placeholder="Seleccione"
                            isClearable
                            className="mt-1"
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    padding: "4px",
                                    borderRadius: "0.375rem",
                                    borderColor: "#d1d5db",
                                    boxShadow: "none",
                                    "&:hover": {
                                        borderColor: "#3b82f6",
                                    },
                                }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Asegurar que el menú se muestre sobre el modal
                            }}
                            menuPortalTarget={document.body} // Renderizar el menú en el body
                            menuPosition="fixed" // Posicionar el menú sin afectar el DOM
                        />
                    </div>

                    {companyMode ? (
                        <div className="col-span-full min-w-0 flex flex-col">
                            <label htmlFor="event_image" className="text-sm font-medium text-gray-700">
                                Imagen del evento
                            </label>
                            <input
                                type="file"
                                id="event_image"
                                name="image"
                                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,image/jpeg,image/png,image/webp,image/gif,image/avif"
                                onChange={handleImageChange}
                                className="mt-1 block max-w-full cursor-pointer rounded-md border border-gray-300 bg-white text-sm text-gray-700 shadow-sm file:mr-4 file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:font-medium file:text-blue-700 hover:file:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                            <span className="mt-1 text-xs text-gray-500">
                                JPG, JPEG, PNG, WEBP, GIF o AVIF. Máximo 5 MB.
                            </span>
                            {imagePreview && (
                                <div className="mt-2 flex h-28 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                                    <img
                                        src={imagePreview}
                                        alt="Vista previa del evento"
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="col-span-full flex flex-col">
                            <label htmlFor="image_path" className="text-sm font-medium text-gray-700">
                                Image Path
                            </label>
                            <input
                                type="text"
                                id="image_path"
                                name="image_path"
                                value={formData.image_path}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-4 transition duration-200 ease-in-out focus:outline-none hover:shadow-md"
                            />
                        </div>
                    )}

                    {companyMode && (
                        <div className="col-span-full flex flex-col">
                            <label htmlFor="sports" className="text-sm font-medium text-gray-700">
                                Deportes
                            </label>
                            <Select
                                inputId="sports"
                                name="sports"
                                options={deportes.map((deporte) => ({
                                    value: deporte.idsport,
                                    label: deporte.name,
                                }))}
                                value={selectedSports}
                                onChange={(options) => setSelectedSports(options || [])}
                                placeholder="Seleccione uno o más deportes"
                                noOptionsMessage={() => "No hay deportes disponibles"}
                                isMulti
                                isSearchable
                                closeMenuOnSelect={false}
                                className="mt-1"
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        padding: "4px",
                                        borderRadius: "0.375rem",
                                        borderColor: "#d1d5db",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "#3b82f6",
                                        },
                                    }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <span className="mt-1 text-xs text-gray-500">
                                Seleccionados: {selectedSports.length}
                            </span>
                        </div>
                    )}

                    <div className="col-span-full">
                        <button
                            type="submit"
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 ease-in-out"
                            disabled={loading} // Deshabilitar mientras carga
                        >
                            {loading ? "Creando..." : "Crear evento"}
                        </button>
                    </div>
                </form>
                {message && (
                    <Typography className="mt-4 text-center text-sm text-gray-700">
                        {message}
                    </Typography>
                )}
            </Sheet>
        </Modal>
    );
};

export default ModalCreateEvent;
